import { useEffect, useMemo, useCallback, useState } from 'react';
import i18n from 'i18next';
import useStateWithLocalStorage from '../helpers/useStateWithLocalStorage';
import { parseJwt } from '../helpers/auth';
import { createCloudinaryWidget } from '../utils/cloudinaryConfig';

export default function useWidget() {
  const [isUploadFront, setIsUploadFront] = useState(false);
  const [isUploadBack, setIsUploadBack] = useState(false);
  const currentLanguageCode = i18n.language || 'en';

  const [documentId, setDocumentId] = useStateWithLocalStorage('documentId');
  const [product, setProduct] = useStateWithLocalStorage('product');
  const [token, setToken] = useStateWithLocalStorage('token');

  const updateDocumentImage = useCallback(async (imageAttribute, imageUrl) => {
    const updateFields = {};
    updateFields[imageAttribute] = imageUrl;
    const settings = {
      method: 'POST',
      headers: {
          Accept: 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
      },
      body: JSON.stringify({_id: documentId, ...updateFields})
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_SERVER_URL}/api/documents`,
      settings,
    );
  }, [documentId, token]);

  useEffect(() => {
    async function createEmptyDocument() {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({"product" : JSON.parse(product)}),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_URL}/api/documents`,
        settings,
      );
      const result = await response.json();
      if (result && result.error) {
        console.log('error during doc update: ' + result.message);
      } else {
        setDocumentId(result);
        console.log(`setting document: ${result}`)
      }
    }

    if (!documentId) {
      console.log('asking for new doc...')
      createEmptyDocument();
    }

  }, []);

  const uploadFolder = useMemo(() => {
    try {
      return parseJwt(token).userId;
    } catch {
      return 'admin_uploads';
    }
  }, [token]);

  const widget1 = useMemo(
    () =>
      createCloudinaryWidget({
        language: currentLanguageCode,
        folder: uploadFolder,
        callback: async (error, result) => {
          if (!error && result && result.event === 'success') {
            console.log(result.info);
            setIsUploadFront(true);
            document
              .getElementById('imagefront')
              .setAttribute('src', result.info.secure_url);
            await updateDocumentImage('nationalLicenseAttributes.frontImageUrl', result.info.secure_url);
          }
        },
      }),
    [currentLanguageCode, uploadFolder, updateDocumentImage],
  );

  const widget2 = useMemo(
    () =>
      createCloudinaryWidget({
        language: currentLanguageCode,
        folder: uploadFolder,
        callback: async (error, result) => {
          if (!error && result && result.event === 'success') {
            console.log(result.info);
            setIsUploadBack(true);
            document
              .getElementById('imageback')
              .setAttribute('src', result.info.secure_url);
            await updateDocumentImage('nationalLicenseAttributes.backImageUrl', result.info.secure_url);
          }
        },
      }),
    [currentLanguageCode, uploadFolder, updateDocumentImage],
  );

  useEffect(() => {
    return () => {
      widget1?.destroy();
      widget2?.destroy();
    };
  }, [widget1, widget2]);

  return {
    isUploadFront,
    setIsUploadFront,
    isUploadBack,
    setIsUploadBack,
    widget1,
    widget2,
    updateDocumentImage,
    uploadFolder,
  };
}
